import moment from 'moment';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import Button from '../../atoms/Buttons';
import DateTimeRange from '../../atoms/DateTimeRange';
import Label from '../../atoms/Label';
import Select from '../../atoms/Select';

type Query = {
  from: moment.Moment | null | undefined;
  to: moment.Moment | null | undefined;
};

type Props = {
  query: Query;
  setQuery: (query: Query) => void;
};

type FormFilterParams = {
  range: {
    from: moment.Moment | null;
    to: moment.Moment | null;
  };
  organizationCode: string | null;
};

const FilterFormWrapper = styled.div`
  margin-top: 16px;

  ${Label}, ${DateTimeRange} {
    margin: 0;
  }

  ${Select} {
    min-width: 200px;
  }
`;

const FilterRow = styled.div`
  margin: 8px 0;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const createFormValues = (query: Query) => ({
  range: {
    from: query.from ?? null,
    to: query.to ?? null,
  },
  organizationCode: query.organizationCode ?? null,
});

const FilterForm = ({ query, setQuery }: Props) => {
  const user = useAppSelector((state) => state.user);

  const { handleSubmit, control, reset } = useForm<FormFilterParams>({
    defaultValues: createFormValues(query),
  });

  const onSubmit = (data: FormFilterParams) => {
    const { range, organizationCode } = data;

    setQuery({
      from: range.from,
      to: range.to,
      ...(user.isServiceUser() ? { organizationCode } : {}),
    });
  };

  useEffect(() => {
    reset(createFormValues(query));
  }, [query, reset]);

  const organizationOptions = [
    { label: '指定なし', value: null },
    ...user
      .moneys()
      .map((money) => ({
        label: money.organization.name,
        value: money.organization.code,
      }))
      .reduce(
        (acc, org) => (acc.find((x) => x.value === org.value) ? acc : [...acc, org]),
        [] as { label: string; value: string }[]
      ),
  ];

  return (
    <FilterFormWrapper>
      <FilterRow>
        {user.isServiceUser() && (
          <>
            <Label>組織名</Label>
            <Controller
              name="organizationCode"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  isMulti={false}
                  options={organizationOptions}
                  value={organizationOptions.find((x) => x.value === value)}
                  onChange={(option) => onChange(option?.value ?? null)}
                />
              )}
            />
          </>
        )}

        <Label>期間</Label>
        <Controller
          name="range"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DateTimeRange
              dateRange={{
                from: value.from,
                to: value.to,
              }}
              setDateRange={(range) => {
                onChange({ from: range.from ?? null, to: range.to ?? null });
              }}
              dateTimeProps={{
                fromProps: { showIcon: true, timeFormat: false },
                toProps: {
                  showIcon: true,
                  timeFormat: false,
                },
              }}
            />
          )}
        />
        <Button onClick={handleSubmit(onSubmit)}>検索</Button>
      </FilterRow>
    </FilterFormWrapper>
  );
};

export default FilterForm;
